import { useState, useEffect } from 'react'
import {Check} from 'lucide-react'
import { lumi } from '../lib/lumi'

// Declare fbq for TypeScript
declare global {
  interface Window {
    fbq?: (action: string, eventName: string, params?: any) => void
  }
}

interface ThankYouProps {
  sessionId: string
}

export function ThankYou({ sessionId }: ThankYouProps) {
  const [isVerifying, setIsVerifying] = useState(true)
  const [isDownloading, setIsDownloading] = useState(false)
  const [verified, setVerified] = useState(false)
  const [email, setEmail] = useState('')
  const [downloadToken, setDownloadToken] = useState('')
  const [error, setError] = useState('')
  const [downloadsRemaining, setDownloadsRemaining] = useState(1)

  const verifyPurchase = async () => {
    setIsVerifying(true)
    setError('')
    
    try {
      const result = await lumi.functions.invoke('verifyPurchase', {
        method: 'POST',
        body: { sessionId }
      })

      if (result.verified) {
        setVerified(true)
        setEmail(result.email)
        setDownloadToken(result.downloadToken)
        
        // Track Purchase event
        if (typeof window.fbq === 'function') {
          window.fbq('track', 'Purchase', {
            content_name: 'The Overthinking Reset',
            content_category: 'Ebook',
            content_type: 'product',
            value: 17.00,
            currency: 'USD'
          })
        }
      } else {
        setError(result.error || 'Unable to verify purchase.')
      }
    } catch (err: any) {
      console.error('Verification error:', err)
      setError('Error verifying payment. Please try again.')
    } finally {
      setIsVerifying(false)
    }
  }

  // Auto-verify on mount
  useEffect(() => {
    verifyPurchase()
  }, [sessionId])

  const handleDownload = async () => {
    setIsDownloading(true)
    setError('')

    try {
      const result = await lumi.functions.invoke('downloadEbook', {
        method: 'POST',
        body: { token: downloadToken }
      })

      if (result.success) {
        setDownloadsRemaining(result.downloadsRemaining)
        // Open download in new tab
        window.open(result.downloadUrl, '_blank')
      } else {
        setError(result.error || 'Error processing download.')
      }
    } catch (err: any) {
      console.error('Download error:', err)
      setError(err?.data?.error || 'Error downloading ebook. Please try again.')
    } finally {
      setIsDownloading(false)
    }
  }

  if (!verified) {
    return (
      <div className="min-h-screen bg-white flex flex-col">
        <div className="flex-grow flex items-center justify-center px-6">
          <div className="max-w-2xl w-full text-center">
            <div className="mb-8">
              <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-6">
                {isVerifying ? (
                  <div className="w-10 h-10 border-4 border-gray-300 border-t-[#2C5F7D] rounded-full animate-spin"></div>
                ) : (
                  <Check className="w-10 h-10 text-gray-400" />
                )}
              </div>
              <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                {isVerifying ? 'Verifying Your Payment...' : 'Payment Verification'}
              </h1>
              <p className="text-lg text-gray-600 mb-8">
                {isVerifying ? 'Please wait while we confirm your purchase.' : 'Unable to verify your purchase automatically.'}
              </p>
            </div>

            {error && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
                <p className="text-red-800 mb-4">{error}</p>
                <button
                  onClick={verifyPurchase}
                  disabled={isVerifying}
                  className="px-8 py-3 bg-[#2C5F7D] text-white font-medium rounded-lg hover:bg-[#234a5f] transition-colors shadow-md disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isVerifying ? 'Verifying...' : 'Try Again'}
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <footer className="py-8 px-6 bg-gray-50 border-t border-gray-200 mt-12">
          <div className="max-w-4xl mx-auto text-center">
            <p className="text-gray-600 mb-2 font-medium">The Overthinking Reset</p>
            <p className="text-sm text-gray-500 mb-4">A 30-Day Mental System for Busy, High-Performing Adults</p>
            <div className="flex items-center justify-center gap-6 text-sm text-gray-500">
              <a href="mailto:support@overthinking-reset.com" className="hover:text-[#2C5F7D] transition-colors">
                Support
              </a>
              <span>•</span>
              <a href="/" className="hover:text-[#2C5F7D] transition-colors">
                Return to Home
              </a>
            </div>
            <p className="text-xs text-gray-400 mt-4">© 2024. All rights reserved.</p>
          </div>
        </footer>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-white flex flex-col">
      <div className="flex-grow flex items-center justify-center px-6 py-12">
        <div className="max-w-2xl w-full text-center">
          <div className="mb-8">
            <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <Check className="w-10 h-10 text-green-600" />
            </div>
            <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Payment Confirmed
            </h1>
            <p className="text-lg text-gray-600 mb-2">
              Thank you for your purchase, <span className="font-medium">{email}</span>
            </p>
            <p className="text-gray-500">
              Your ebook is ready to download.
            </p>
          </div>

          <div className="bg-gray-50 rounded-2xl p-8 mb-6">
            <div className="mb-6">
              <img
                src="https://static.lumi.new/6c/6c05a211929862748872639e568c1478.png"
                alt="The Overthinking Reset"
                className="w-48 mx-auto shadow-lg rounded-lg mb-6"
              />
              <h2 className="text-2xl font-bold text-gray-900 mb-2">
                The Overthinking Reset
              </h2>
              <p className="text-gray-600">
                A 30-Day Mental System for Busy, High-Performing Adults
              </p>
            </div>

            {error && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
                <p className="text-red-800">{error}</p>
              </div>
            )}

            <button
              onClick={handleDownload}
              disabled={isDownloading}
              className="w-full px-10 py-5 bg-[#2C5F7D] text-white text-lg font-medium rounded-lg hover:bg-[#234a5f] transition-colors shadow-md disabled:opacity-50 disabled:cursor-not-allowed mb-4"
            >
              {isDownloading ? 'Processing...' : 'Download Ebook (PDF)'}
            </button>

            <p className="text-sm text-gray-500">
              Downloads remaining: {downloadsRemaining} of 1
            </p>
          </div>

          <div className="space-y-4 text-sm text-gray-600 mb-12">
            <p className="flex items-center justify-center gap-2">
              <Check className="w-4 h-4 text-[#2C5F7D]" />
              Lifetime access to the ebook
            </p>
            <p className="flex items-center justify-center gap-2">
              <Check className="w-4 h-4 text-[#2C5F7D]" />
              PDF format compatible with all devices
            </p>
            <p className="flex items-center justify-center gap-2">
              <Check className="w-4 h-4 text-[#2C5F7D]" />
              Email support within 24 hours
            </p>
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="py-8 px-6 bg-gray-50 border-t border-gray-200">
        <div className="max-w-4xl mx-auto text-center">
          <p className="text-gray-600 mb-2 font-medium">The Overthinking Reset</p>
          <p className="text-sm text-gray-500 mb-4">A 30-Day Mental System for Busy, High-Performing Adults</p>
          <div className="flex items-center justify-center gap-6 text-sm text-gray-500">
            <a href="mailto:support@overthinking-reset.com" className="hover:text-[#2C5F7D] transition-colors">
              Support
            </a>
            <span>•</span>
            <a href="/" className="hover:text-[#2C5F7D] transition-colors">
              Return to Home
            </a>
          </div>
          <p className="text-xs text-gray-400 mt-4">© 2024. All rights reserved.</p>
        </div>
      </footer>
    </div>
  )
}
